import clsx from 'clsx';
import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import PageMeta from '../../components/common/PageMeta';
import { FillPage } from '../../layout/FillPage';
import { formatShortDateTime, relativeTime } from '../../shared/time';
import { useHeld, useLeave } from '../../shared/useLeave';
import type { KnowledgeQuestionListItem } from '../../types/knowledgeQuestions';
import {
  Button,
  Callout,
  EmptyState,
  IconArrowRight,
  IconCheckCircle,
  IconChevronLeft,
  IconClose,
  IconInfo,
  Reason,
  RefreshFailed,
  Skeleton,
  Workspace,
} from '../../ui/v2';
import { FaqEditor } from './FaqEditor';
import {
  useKnowledgeActions,
  useKnowledgeQuestion,
  useOpenKnowledgeQuestions,
} from './knowledgeData';
import {
  ADDITIONAL_FAQS_HREF,
  callHref,
  KNOWLEDGE_QUESTIONS_BASE,
  occurrenceLabel,
} from './knowledgeModel';

/**
 * Knowledge questions: what callers asked that Arthur couldn't answer. One
 * list of questions still waiting. Answering one adds it to Additional FAQs
 * (checked and published there); dismissing drops it. Either way it leaves
 * the list; anything left alone stays.
 */
export default function KnowledgeQuestions() {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const list = useOpenKnowledgeQuestions();
  const rows = list.data?.results ?? [];
  const selectedId = id && /^\d+$/.test(id) ? Number(id) : null;
  const [added, setAdded] = useState<string | null>(null);

  const open = (q: KnowledgeQuestionListItem | null) =>
    navigate(
      q ? `${KNOWLEDGE_QUESTIONS_BASE}/${q.id}` : KNOWLEDGE_QUESTIONS_BASE,
    );

  const openCount = list.data?.count ?? 0;

  return (
    <FillPage>
      <PageMeta
        title="AskArthur | Questions to answer"
        description="Questions callers asked that Arthur couldn't answer"
      />
      <Workspace
        title="Questions to answer"
        description="Callers asked these and Arthur didn’t have the answer. Add one and Arthur will use it on the next call."
        meta={list.data ? `${openCount} need an answer` : undefined}
      >
        <div className="flex min-h-0 flex-1">
          <div
            className={clsx(
              'flex min-h-0 w-full flex-col bg-v2-surface lg:w-[400px] lg:shrink-0 lg:border-r lg:border-v2-line',
              selectedId != null && 'hidden lg:flex',
            )}
          >
            {added && (
              <Callout
                tone="accent"
                icon={<IconCheckCircle size={14} />}
                className="m-3"
              >
                <span role="status">
                  Added to{' '}
                  <Link
                    to={ADDITIONAL_FAQS_HREF}
                    className="text-v2-accent hover:text-v2-accent-hover"
                  >
                    Additional FAQs
                  </Link>
                  : “{added}”
                </span>
              </Callout>
            )}
            {list.isRefetchError && list.data && (
              <RefreshFailed
                className="m-3"
                what="Questions"
                onRetry={() => void list.refetch()}
              />
            )}
            {list.isError && !list.data ? (
              <div className="p-4">
                <Callout tone="danger" title="Couldn’t load questions">
                  {list.error.message}
                  <div className="mt-2">
                    <Button size="sm" onClick={() => void list.refetch()}>
                      Retry
                    </Button>
                  </div>
                </Callout>
              </div>
            ) : list.isPending ? (
              <ListSkeleton />
            ) : rows.length === 0 ? (
              <EmptyState
                icon={<IconCheckCircle />}
                title="Nothing waiting"
                description="When a caller asks something Arthur can’t answer, it shows up here."
              />
            ) : (
              <ul
                role="listbox"
                aria-label="Questions to answer"
                className="v2-scroll min-h-0 flex-1 overflow-y-auto"
              >
                {rows.map((q) => (
                  <QuestionRow
                    key={q.id}
                    q={q}
                    selected={q.id === selectedId}
                    onOpen={() => open(q)}
                  />
                ))}
              </ul>
            )}
          </div>

          <div
            className={clsx(
              'flex min-h-0 min-w-0 flex-1 flex-col bg-v2-surface',
              selectedId == null && 'hidden lg:flex',
            )}
          >
            {selectedId != null ? (
              <Detail
                key={selectedId}
                id={selectedId}
                onBack={() => open(null)}
                onAnswered={(question) => setAdded(question)}
              />
            ) : (
              <div className="v2-fade-in">
                <EmptyState
                  icon={<IconInfo />}
                  title="Select a question"
                  description="Pick one on the left to see what the caller asked and write Arthur’s answer."
                />
              </div>
            )}
          </div>
        </div>
      </Workspace>
    </FillPage>
  );
}

const ListSkeleton: React.FC = () => (
  <ul aria-label="Loading questions" className="flex flex-col">
    {Array.from({ length: 5 }).map((_, i) => (
      <li key={i} className="border-b border-v2-line/80 px-4 py-3">
        <Skeleton className="h-3" width={`${55 + (i % 3) * 12}%`} />
        <Skeleton className="mt-2 h-3" width="30%" />
      </li>
    ))}
  </ul>
);

const QuestionRow: React.FC<{
  q: KnowledgeQuestionListItem;
  selected: boolean;
  onOpen: () => void;
}> = ({ q, selected, onOpen }) => {
  const ref = useRef<HTMLLIElement>(null);
  useEffect(() => {
    if (selected) ref.current?.scrollIntoView?.({ block: 'nearest' });
  }, [selected]);
  return (
    <li
      ref={ref}
      role="option"
      aria-selected={selected}
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => e.key === 'Enter' && onOpen()}
      className={clsx(
        'flex cursor-pointer flex-col gap-1 border-b border-v2-line/80 px-4 py-3 outline-none',
        selected
          ? 'bg-v2-accent/[0.07]'
          : 'hover:bg-v2-sunken/70 focus-visible:bg-v2-sunken',
      )}
    >
      <span className="line-clamp-2 text-v2-13 font-medium text-v2-fg">
        {q.question}
      </span>
      <span className="flex items-center gap-2 text-v2-12 text-v2-fg-3">
        <span className="v2-num">{occurrenceLabel(q.occurrence_count)}</span>
        <span aria-hidden>·</span>
        <time dateTime={q.last_seen_at}>{relativeTime(q.last_seen_at)}</time>
      </span>
    </li>
  );
};

// ---------------------------------------------------------------------------
// Detail
// ---------------------------------------------------------------------------

/**
 * One question. Answering adds it to Additional FAQs and dismissing drops
 * it; both take it off the list, so the panel slides out and the list is
 * left selected on nothing. A question already answered or dismissed (an
 * old link) shows where it went instead.
 */
const Detail: React.FC<{
  id: number;
  onBack: () => void;
  onAnswered: (question: string) => void;
}> = ({ id, onBack, onAnswered }) => {
  const detail = useKnowledgeQuestion(id);
  const actions = useKnowledgeActions();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { leaving, leave } = useLeave(onBack);
  const q = useHeld(detail.data, leaving);

  if (detail.isError && !detail.data) {
    return (
      <div className="p-6">
        <Callout tone="danger" title="Couldn’t load this question">
          {detail.error.message}
          <div className="mt-2 flex gap-2">
            <Button size="sm" onClick={() => void detail.refetch()}>
              Retry
            </Button>
            <Button size="sm" variant="ghost" onClick={onBack}>
              Back to questions
            </Button>
          </div>
        </Callout>
      </div>
    );
  }
  if (!q) return <DetailSkeleton />;

  const waiting = q.status === 'open';

  const dismiss = async () => {
    setBusy(true);
    setError(null);
    try {
      await leave(() => actions.dismiss(q.id));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      aria-label={`Question: ${q.question}`}
      className={clsx(
        'flex min-h-0 flex-1 flex-col',
        leaving && 'v2-slide-out-right',
      )}
    >
      <header className="flex shrink-0 items-start gap-3 border-b border-v2-line px-4 py-3 lg:px-6">
        <Button
          size="sm"
          variant="ghost"
          leading={<IconChevronLeft size={14} />}
          onClick={onBack}
          className="-ml-2 lg:hidden"
        >
          Questions
        </Button>
        <div className="min-w-0 flex-1">
          <h2 className="text-v2-16 font-semibold tracking-[-0.01em]">
            {q.question}
          </h2>
          <p className="mt-1 flex flex-wrap items-center gap-2 text-v2-12 text-v2-fg-3">
            <span className="v2-num">
              {occurrenceLabel(q.occurrence_count)}
            </span>
            <span aria-hidden>·</span>
            <span>
              Last asked{' '}
              <time dateTime={q.last_seen_at}>
                {formatShortDateTime(q.last_seen_at)}
              </time>
            </span>
          </p>
        </div>
        {waiting && (
          <Button
            size="sm"
            variant="ghost"
            leading={<IconClose size={14} />}
            loading={busy}
            onClick={() => void dismiss()}
          >
            Dismiss
          </Button>
        )}
      </header>

      <div className="v2-scroll min-h-0 flex-1 overflow-y-auto">
        <div className="max-w-[760px] px-4 py-6 lg:px-8">
          {error && (
            <Callout
              tone="danger"
              className="mb-5"
              title="That didn’t go through"
            >
              {error}
            </Callout>
          )}

          <h3 className="text-v2-13 font-semibold text-v2-fg">
            Arthur’s answer
          </h3>
          {q.status === 'dismissed' ? (
            <Reason className="mt-2">Dismissed.</Reason>
          ) : !waiting ? (
            <Reason className="mt-2">
              This one is in{' '}
              <Link
                to={ADDITIONAL_FAQS_HREF}
                className="text-v2-accent hover:text-v2-accent-hover"
              >
                Additional FAQs
              </Link>
              .
            </Reason>
          ) : (
            <>
              <p className="mt-0.5 text-v2-12 text-v2-fg-3">
                Your answer goes to{' '}
                <Link
                  to={ADDITIONAL_FAQS_HREF}
                  className="text-v2-accent hover:text-v2-accent-hover"
                >
                  Additional FAQs
                </Link>
                . Arthur uses it once it’s published there.
              </p>
              <div className="mt-4">
                <FaqEditor
                  faq={null}
                  initialQuestion={q.question}
                  createLabel="Add to Additional FAQs"
                  canPublish={false}
                  publishEnabled={false}
                  onSave={(text) =>
                    leave(async () => {
                      await actions.answer(q.id, text);
                      onAnswered(text.question);
                    })
                  }
                  onCheck={actions.checkFaq}
                  onPublish={actions.publishFaq}
                />
              </div>
            </>
          )}

          <h3 className="mt-8 border-t border-v2-line pt-6 text-v2-13 font-semibold text-v2-fg">
            Calls
          </h3>
          {q.occurrences.length === 0 ? (
            <Reason className="mt-2">No calls kept for this question.</Reason>
          ) : (
            <ul className="mt-1 divide-y divide-v2-line">
              {q.occurrences.map((o) => {
                const when = formatShortDateTime(o.occurred_at);
                return (
                  <li key={o.id}>
                    <Link
                      to={callHref(o.retell_call_id)}
                      className="flex items-center justify-between gap-3 py-2.5 text-v2-13 text-v2-fg hover:text-v2-accent"
                    >
                      <time dateTime={o.occurred_at} className="v2-num">
                        {when}
                      </time>
                      <span className="inline-flex items-center gap-1 text-v2-12 font-medium text-v2-accent">
                        View call
                        <IconArrowRight size={12} />
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
          {q.occurrence_count > q.occurrences.length && (
            <Reason className="mt-3">
              Showing the latest {q.occurrences.length} of {q.occurrence_count}{' '}
              calls.
            </Reason>
          )}
        </div>
      </div>
    </section>
  );
};

const DetailSkeleton: React.FC = () => (
  <div aria-label="Loading question" className="px-4 py-5 lg:px-6">
    <Skeleton className="h-4" width="60%" />
    <Skeleton className="mt-3 h-3" width="30%" />
    <Skeleton className="mt-8 h-16" width="100%" />
  </div>
);
